import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Link,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Alert
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import StarIcon from "@mui/icons-material/Star";
import { useTranslation } from "react-i18next";
import { GoogleLogin } from "@react-oauth/google";
import MyAppHeader from "./MyAppHeader";
import { withCountryPrefix } from "./utils/routeHelpers";
import { trackEvent } from "../utils/analytics/gtag";
import AppleSignInButton from "./auth/AppleSignInButton";
import api from "../services/api";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "el", label: "Ελληνικά" },
  { code: "sr", label: "Srpski" },
  { code: "ro", label: "Română" },
  { code: "bg", label: "Български" },
  { code: "mk", label: "Македонски" },
  { code: "al", label: "Shqip" },
  { code: "hu", label: "Magyar" },
];

const Register = () => {
  const { register, registerWithGoogle, registerWithApple } = useAuth();
  const { t, i18n } = useTranslation();

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    language: i18n.language || "el",
    desired_plan: "free", // "free" | "premium"
  });

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState({
    apple: false,
    google: false,
    facebook: false,
    apple_client_id: "",
    apple_redirect_uri: "",
  });

  const navigate = useNavigate();
  const location = useLocation();

  const match = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
  const country = match ? match[1] : "el";
  const query = new URLSearchParams(location.search || "");
  const returnToQuery = query.get("returnTo");
  const intentQuery = (query.get("intent") || "").trim().toLowerCase();
  const planQueryRaw = (query.get("plan") || "").trim().toLowerCase();
  const planQuery = planQueryRaw === "yearly" ? "yearly" : "monthly";
  const stateReturnTo = location.state?.returnTo;
  const returnTo = returnToQuery || stateReturnTo;
  const premiumIntentExplicit =
    intentQuery === "premium" ||
    /(?:\?|&)intent=premium(?:&|$)/i.test(String(returnTo || ""));

  const premiumReturnTo = withCountryPrefix(
    `/subscription?intent=premium&plan=${planQuery}`,
    country
  );
  const resolvedPremiumReturnTo = premiumIntentExplicit ? (returnTo || premiumReturnTo) : null;
  const funnelParams = {
    lang: country,
    country,
    plan: planQuery,
    currency: null,
    intent: premiumIntentExplicit ? "premium" : null,
    user_tier: "guest",
  };

  useEffect(() => {
    trackEvent("funnel_register_view", funnelParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    api.users
      .getAuthProviders()
      .then((response) => {
        if (!active) return;
        const payload = response?.data || {};
        setProviders({
          apple: Boolean(payload.apple),
          google: Boolean(payload.google),
          facebook: Boolean(payload.facebook),
          apple_client_id: String(payload.apple_client_id || "").trim(),
          apple_redirect_uri: String(payload.apple_redirect_uri || "").trim(),
        });
      })
      .catch(() => {
        if (!active) return;
        setProviders({
          apple: false,
          google: false,
          facebook: false,
          apple_client_id: "",
          apple_redirect_uri: "",
        });
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!premiumIntentExplicit) return;
    setForm((prev) => {
      if (prev.desired_plan === "premium") return prev;
      return { ...prev, desired_plan: "premium" };
    });
  }, [premiumIntentExplicit]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    try {
      if (premiumIntentExplicit) {
        window.sessionStorage.setItem("postAuthRedirect", resolvedPremiumReturnTo || premiumReturnTo);
      } else {
        window.sessionStorage.removeItem("postAuthRedirect");
      }
    } catch (_err) {
      // non-blocking
    }
    return undefined;
  }, [premiumIntentExplicit, resolvedPremiumReturnTo, premiumReturnTo]);

  const consumePostAuthRedirect = () => {
    if (typeof window === "undefined") return null;
    try {
      const value = window.sessionStorage.getItem("postAuthRedirect");
      if (value) {
        window.sessionStorage.removeItem("postAuthRedirect");
      }
      if (value && typeof value === "string" && value.startsWith("/")) {
        return value;
      }
      return null;
    } catch (_err) {
      return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlanChange = (_e, value) => {
    if (!value) return;
    setForm((prev) => ({ ...prev, desired_plan: value }));
  };

  const redirectAfterAuth = () => {
    // Explicit premium intent from CTA has priority.
    if (resolvedPremiumReturnTo) {
      navigate(resolvedPremiumReturnTo, { replace: true });
      return;
    }

    // αν ο χρήστης διάλεξε premium, πάμε subscription
    if (form.desired_plan === "premium") {
      navigate(premiumReturnTo, { replace: true });
      return;
    }

    // αλλιώς → όπου πρέπει (returnTo ή dashboard)
    navigate(returnTo || withCountryPrefix("/client/dashboard", country), {
      replace: true,
    });
  };

  // --- GOOGLE SIGN UP ---
  const handleGoogleSignUp = async (credentialResponse) => {
    setError(null);
    setLoading(true);
    try {
      const res = await registerWithGoogle(
        credentialResponse.credential,
        { desired_plan: form.desired_plan } // ✅ στέλνουμε και plan
      );

      if (res?.success) {
        trackEvent("funnel_register_success", funnelParams);
        const postAuthRedirect = consumePostAuthRedirect();
        if (postAuthRedirect) {
          navigate(postAuthRedirect, { replace: true });
        } else {
          redirectAfterAuth();
        }
      } else {
        setError(res?.message || t("registration_failed"));
      }
    } catch (err) {
      setError(err?.message || t("registration_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignUp = async ({ idToken, user }) => {
    setError(null);
    setLoading(true);
    try {
      const res = await registerWithApple({
        idToken,
        user,
        email: user?.email || null,
        desired_plan: form.desired_plan,
      });

      if (res?.success) {
        trackEvent("funnel_register_success", funnelParams);
        const postAuthRedirect = consumePostAuthRedirect();
        if (postAuthRedirect) {
          navigate(postAuthRedirect, { replace: true });
        } else {
          redirectAfterAuth();
        }
      } else {
        setError(res?.message || t("registration_failed"));
      }
    } catch (err) {
      setError(err?.message || t("registration_failed"));
    } finally {
      setLoading(false);
    }
  };

  // --- FACEBOOK SIGN UP ---
  // const handleFacebookSignUp = async (fbResponse) => {
  //   setError(null);
  //   setLoading(true);
  //   try {
  //     const token = fbResponse?.accessToken;
  //     const res = await registerWithFacebook(token, { desired_plan: form.desired_plan });

  //     if (res?.success) {
  //       trackEvent("funnel_register_success", funnelParams);
  //       redirectAfterAuth();
  //     } else {
  //       setError(res?.message || t("registration_failed"));
  //     }
  //   } catch (err) {
  //     setError(err?.message || t("registration_failed"));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // --- EMAIL/PASSWORD SIGN UP ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    trackEvent("funnel_register_submit", funnelParams);
    try {
      const res = await register(
        form.username,
        form.email,
        form.password,
        form.language,
        form.desired_plan // ✅ NEW
      );

      if (res?.success) {
        trackEvent("funnel_register_success", funnelParams);
        // explicit premium intent from CTA takes priority
        if (resolvedPremiumReturnTo) {
          navigate(resolvedPremiumReturnTo, { replace: true });
          return;
        }

        // premium -> subscription
        if (form.desired_plan === "premium") {
          navigate(premiumReturnTo, { replace: true });
          return;
        }

        // free -> login
        const loginPath = withCountryPrefix("/login", country);
        const qs = new URLSearchParams();
        if (returnTo) qs.set("returnTo", returnTo);
        navigate(qs.toString() ? `${loginPath}?${qs.toString()}` : loginPath, { replace: true });
      } else {
        setError(res?.message || t("registration_failed"));
      }
    } catch (err) {
      setError(err?.message || t("an_error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  const isPremiumSelected = form.desired_plan === "premium";

  return (
    <>
      <MyAppHeader />
      <Box sx={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Paper
          elevation={4}
          sx={{
            p: 4,
            maxWidth: 420,
            width: "100%",
            borderRadius: 4,
            boxShadow: "0 6px 36px 0 #49bc9050",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <PersonAddIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {t("register_title") || "Create account"}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {premiumIntentExplicit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {(t("premium_one_step_banner") || "You're 1 step away from Premium")} {" "}
              <strong>({planQuery === "yearly" ? "Yearly" : "Monthly"})</strong>
            </Alert>
          )}

          {/* ✅ PLAN TOGGLE (Premium Pro look) */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              {i18n.language === "el" ? "Επιλογή πλάνου" : "Choose a plan"}
            </Typography>

            <ToggleButtonGroup
              value={form.desired_plan}
              exclusive
              onChange={handlePlanChange}
              fullWidth
              sx={{
                "& .MuiToggleButton-root": { py: 1.2, fontWeight: 700, borderRadius: 2 },
              }}
            >
              <ToggleButton value="free">
                {i18n.language === "el" ? "Free" : "Free"}
              </ToggleButton>

              <ToggleButton value="premium">
                <StarIcon sx={{ mr: 1 }} fontSize="small" />
                {i18n.language === "el" ? "Premium Pro" : "Premium Pro"}
              </ToggleButton>
            </ToggleButtonGroup>

            {isPremiumSelected && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {i18n.language === "el"
                  ? "Θα ολοκληρώσεις την αναβάθμιση στο επόμενο βήμα (Stripe)."
                  : "You’ll complete the upgrade in the next step (Stripe)."}
              </Alert>
            )}
          </Box>

          {/* --- SOCIAL SIGN UP --- */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2, width: "100%" }}>
            <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
              {providers.google ? (
                <Box
                  sx={{
                    width: "100%",
                    "& iframe": { width: "100% !important" },
                    "& > div": { width: "100% !important" },
                  }}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSignUp}
                    onError={() => setError(t("google_sign_up_failed"))}
                    useOneTap={false}
                  />
                </Box>
              ) : null}
              {providers.apple ? (
                <AppleSignInButton
                  label={t("sign_up_with_apple")}
                  onSuccess={handleAppleSignUp}
                  onError={(err) => setError(err?.message || t("apple_sign_up_failed"))}
                  disabled={loading}
                  clientId={providers.apple_client_id}
                  redirectURI={providers.apple_redirect_uri}
                />
              ) : null}

              {/* <FacebookRedirectLogin
                onSuccess={handleFacebookSignUp}
                onFailure={() => setError("Facebook Sign up failed")}
                disabled={loading}
                postAuthRedirect={premiumIntentExplicit ? (resolvedPremiumReturnTo || premiumReturnTo) : null}
              >
                {t("sign_up_with_facebook") || "Εγγραφή με Facebook"}
              </FacebookRedirectLogin> */}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("or_sign_up_with_email") || "ή εγγραφή με email"}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <form onSubmit={handleSubmit}>
            <TextField
              label={t("username") || "Username"}
              name="username"
              fullWidth
              margin="normal"
              value={form.username}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <TextField
              label={t("email") || "Email"}
              name="email"
              type="email"
              fullWidth
              margin="normal"
              value={form.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <TextField
              label={t("password") || "Password"}
              name="password"
              type="password"
              fullWidth
              margin="normal"
              value={form.password}
              onChange={handleChange}
              required
              disabled={loading}
            />

            {/* Language */}
            <FormControl fullWidth margin="normal" disabled={loading}>
              <InputLabel>{t("preferred_language") || "Γλώσσα προτίμησης"}</InputLabel>
              <Select
                name="language"
                value={form.language}
                label={t("preferred_language") || "Γλώσσα προτίμησης"}
                onChange={handleChange}
              >
                {LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              sx={{ mt: 2, py: 1.2, fontWeight: 800, borderRadius: 999 }}
              disabled={loading}
            >
              {loading
                ? (i18n.language === "el" ? "Γίνεται εγγραφή..." : "Creating account...")
                : (t("register_btn") || "Register")}
            </Button>
          </form>

          <Box sx={{ textAlign: "right", mt: 2 }}>
            <Link
              component="button"
              underline="hover"
              onClick={() => {
                const loginPath = withCountryPrefix("/login", country);
                const qs = new URLSearchParams();
                if (intentQuery === "premium") {
                  qs.set("intent", "premium");
                  qs.set("plan", planQuery);
                }
                if (returnTo) qs.set("returnTo", returnTo);
                navigate(qs.toString() ? `${loginPath}?${qs.toString()}` : loginPath);
              }}
            >
              {t("already_account_login") || "Έχεις λογαριασμό; Σύνδεση"}
            </Link>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Register;
